import type { AxiosRequestConfig, AxiosResponse } from "axios";

import { type Organisation } from "shared/stores/user/types";
import type { StreamGroup } from "shared/types";
import type { FeatureKey } from "shared/types/features";
import type { Nullable } from "shared/types/utility";

import ApiClient from "./ApiClient";

export interface OrganisationActivity {
  created_at: string;
  feature_id: number;
  id: number;
  organisation_id: number;
  target_id: number;
  target_type: string;
  user_id: number;
}

export interface OrganisationFeature {
  contract_allowance?: number;
  cost?: number;
  credits?: number;
  total_used_credits?: number;
  used_credits?: number;
}

export type OrganisationFeatures = Partial<
  Record<FeatureKey, OrganisationFeature>
>;

export interface OrganisationNewsSummary {
  date: string;
  digest: string;
  featured: boolean;
  id: number;
  published_at: string;
  read?: boolean;
  text: string;
  title: string;
}

export interface OrganisationReportSpec {
  delivery_time: number;
  id: number;
}

export interface OrganisationStream {
  id: number;
  label: string;
  total_user_count: number;
  user: {
    name: string;
    team_name: string;
  };
}

export interface OrganisationStreamLimitResponse {
  streams_limit_reached: boolean;
}

export interface OrganisationUsageTeam {
  active_users: string[];
  id: number;
  logo_url: Nullable<string>;
  name: string;
}

export interface OrganisationUsage {
  active_users_count: number;
  ai_visibility_credits_consumed: Nullable<number>;
  ai_visibility_credits_period_end: Nullable<string>;
  ai_visibility_credits_period_start: Nullable<string>;
  ai_visibility_credits_subscription_active: Nullable<boolean>;
  ai_visibility_credits_synced_at: Nullable<string>;
  ai_visibility_credits_total: Nullable<number>;
  ai_visibility_max_prompts: Nullable<number>;
  ai_visibility_max_sources: Nullable<number>;
  dashboards_with_ai_visibility_count?: Nullable<number>;
  instant_insights_reports_with_monitoring_count?: Nullable<number>;
  managed_briefs_count?: number;
  max_dashboards_with_ai_visibility: Nullable<number>;
  max_instant_insights_reports_with_monitoring: Nullable<number>;
  max_social_streams_count: number;
  max_streams_count: number;
  max_teams_count: number;
  max_users_count: number;
  reports_this_month?: number;
  social_streams_count: number;
  streams_count: number;
  teams: OrganisationUsageTeam[];
  usage_by_month: Record<string, Record<string, number>>;
}

export interface OrganisationStreamGroupsParams {
  label?: string;
  organisation_team_id?: Nullable<number>;
  page?: number;
  page_size?: number;
  stream_type?: string | string[];
}

export interface OrganisationStreamParams {
  page?: number;
  page_size?: number;
  query?: string;
  stream_type?: string;
}

export interface OrganisationActivityParams {
  limit?: number;
  target_id?: number;
  target_type?: string;
}

export interface OrganisationNewsSummaryParams {
  date?: string;
  handleErrors?: boolean;
}

class OrganisationsService extends ApiClient {
  constructor() {
    super("/organisations");
  }

  activities(
    organisationId: number,
    options: AxiosRequestConfig<OrganisationActivityParams> = {}
  ): Promise<AxiosResponse<OrganisationActivity[]>> {
    return this.get<OrganisationActivity[]>(
      `/${organisationId}/activities`,
      options
    );
  }

  feature(
    organisationId: number,
    feature: FeatureKey,
    options: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<OrganisationFeature>> {
    return this.get<OrganisationFeature>(
      `/${organisationId}/features/${feature}`,
      options
    );
  }

  features(
    organisationId: number,
    options: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<OrganisationFeatures>> {
    return this.get<OrganisationFeatures>(
      `/${organisationId}/features`,
      options
    );
  }

  newsSummaries(
    organisationId: number,
    options: AxiosRequestConfig<OrganisationNewsSummaryParams> = {}
  ): Promise<AxiosResponse<OrganisationNewsSummary[]>> {
    return this.get<OrganisationNewsSummary[]>(
      `/${organisationId}/news_summaries`,
      options
    );
  }

  reportSpecs<T extends OrganisationReportSpec = OrganisationReportSpec>(
    organisationId: number,
    options: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<T[]>> {
    return this.get<T[]>(
      `/${organisationId}/organisation_report_spec`,
      options
    );
  }

  show<T = Organisation>(
    organisationId: number,
    options: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<T>> {
    return this.get<T>(`/${organisationId}`, options);
  }

  streamGroups(
    organisationId: number,
    options: AxiosRequestConfig<OrganisationStreamGroupsParams> = {}
  ): Promise<AxiosResponse<StreamGroup[]>> {
    return this.get<StreamGroup[]>(`/${organisationId}/stream_groups`, options);
  }

  streams(
    organisationId: number,
    options: AxiosRequestConfig<OrganisationStreamParams> = {}
  ): Promise<AxiosResponse<OrganisationStream[]>> {
    return this.get<OrganisationStream[]>(
      `/${organisationId}/streams`,
      options
    );
  }

  streamsLimitReached(
    organisationId: number,
    streamType: string,
    options: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<OrganisationStreamLimitResponse>> {
    return this.get<OrganisationStreamLimitResponse>(
      `/${organisationId}/streams_limit_reached`,
      {
        ...options,
        params: {
          ...options.params,
          stream_type: streamType,
        },
      }
    );
  }

  usage(
    organisationId: number,
    options: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<OrganisationUsage>> {
    return this.get<OrganisationUsage>(`/${organisationId}/usage`, options);
  }
}

export default new OrganisationsService();
